import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import WarningPage from './WarningPage';
import WarningLoading from './WarningLoading';
import RetryNotice from './RetryNotice';
import WarningMetricTable from './WarningMetricTable';
import apiRequest from '../../api/request';
import { leaveApproveRoute, readViewer } from './approvalRoutes';

// Department Leave Approvals - the leave requests the "Leave - Pending RH / HR Approval" cards counted, as a page of
// their own (the cards used to open the WFH GRANT requests page, a different request type).
//   - not limited to a month: a request is listed for as long as it is pending, like the card's count;
//   - filterable by the requester's department;
//   - every row's Review button opens the app's standard approve screen (HR's or the reporting head's), so the
//     approval itself - balance checks, attendance write-back - is still done by the existing code, not re-built here.
// Who sees what is decided on the server (pending_approvals_dashboard.controller.js): a reporting head gets the RH
// stage for their own team, HR gets both stages for everyone, anyone else gets neither.
const STAGES = {
  rh: { cardKey: 'leave-rh', label: 'Pending RH approval', hint: 'Waiting on the reporting head. A request still unanswered after 48 hours is passed to HR automatically.' },
  hr: { cardKey: 'leave-hr', label: 'Pending HR approval', hint: 'The reporting head has approved or the 48 hours passed - now waiting on HR.' },
};
const STAGE_ORDER = ['rh', 'hr'];

const day = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '-');

const DepartmentLeaveApprovals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request
  const viewer = useMemo(() => readViewer(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: 'warning-dashboard/pending-approvals-dashboard', method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js), so the status check is required
        if (res?.status !== 'success') {
          setError(res?.message || 'Could not load the pending leave approvals');
          return;
        }
        setCards(res?.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || 'Could not load the pending leave approvals');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // only the stages this viewer is allowed to see come back from the server at all
  const available = useMemo(() => STAGE_ORDER.filter((st) => cards.some((c) => c.key === STAGES[st].cardKey)), [cards]);
  const requested = searchParams.get('stage');
  const stage = available.includes(requested) ? requested : available[0] || null;
  const card = stage ? cards.find((c) => c.key === STAGES[stage].cardKey) : null;
  const countOf = (st) => cards.find((c) => c.key === STAGES[st].cardKey)?.count ?? 0;

  const rows = useMemo(() => (card ? card.items.map((i) => ({ ...i, entityId: i.leave_id })) : []), [card]);
  const departmentNames = useMemo(() => {
    const names = new Map();
    rows.forEach((r) => {
      if (r.departmentId !== null && r.departmentId !== undefined) names.set(Number(r.departmentId), r.departmentName || `Department ${r.departmentId}`);
    });
    return names;
  }, [rows]);

  const columns = [
    { field: 'employeeName', headerName: 'Employee' },
    { field: 'departmentName', headerName: 'Department', render: (r) => r.departmentName || 'No department', csvValue: (r) => r.departmentName || 'No department' },
    { field: 'leaveType', headerName: 'Leave type' },
    { field: 'from_date', headerName: 'From', render: (r) => day(r.from_date), csvValue: (r) => day(r.from_date) },
    { field: 'to_date', headerName: 'To', render: (r) => day(r.to_date), csvValue: (r) => day(r.to_date) },
    { field: 'no_of_days', headerName: 'Days', align: 'right' },
    { field: 'app_timestamp', headerName: 'Applied on', render: (r) => (r.app_timestamp ? dayjs(r.app_timestamp).format('DD MMM YYYY, HH:mm') : '-'), csvValue: (r) => (r.app_timestamp ? dayjs(r.app_timestamp).format('DD MMM YYYY, HH:mm') : '') },
    {
      field: 'hoursPending',
      headerName: 'Waiting',
      align: 'right',
      render: (r) => (
        <Typography variant="body2" fontWeight={Number(r.hoursPending) > 48 ? 700 : 400} color={Number(r.hoursPending) > 48 ? 'error.main' : 'text.primary'}>
          {r.hoursPending}h
        </Typography>
      ),
      csvValue: (r) => r.hoursPending,
    },
    {
      field: 'actions', // never exported (makeCSV skips 'actions')
      headerName: '',
      align: 'right',
      render: (r) => (
        <Button component={Link} to={leaveApproveRoute(r.leave_id, stage, viewer)} size="small" variant="contained" color="warning">
          Review
        </Button>
      ),
    },
  ];

  return (
    <WarningPage
      title="Department Leave Approvals"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to="/dashboard/warning-dashboard/pending-approvals" variant="text">All pending approvals</Button>
          <Button component={Link} to="/dashboard/warning-dashboard" variant="text">Back to Warning Dashboard</Button>
        </Box>
      }
    >
      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && available.length === 0 && (
        <Alert severity="info">
          There are no leave approvals for you here. This page lists the leave requests waiting on a reporting head (their team) or on HR.
        </Alert>
      )}

      {!loading && !error && card && (
        <>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
            {available.map((st) => (
              <Chip
                key={st}
                clickable
                color={st === stage ? 'primary' : 'default'}
                variant={st === stage ? 'filled' : 'outlined'}
                label={`${STAGES[st].label} · ${countOf(st)}`}
                onClick={() => setSearchParams({ stage: st })}
              />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {STAGES[stage].hint} Every request still pending is listed, not only this month's. Review opens the standard approval screen.
          </Typography>
          <WarningMetricTable
            rows={rows}
            columns={columns}
            searchFields={['employeeName', 'leaveType', 'departmentName']}
            departmentName={(id) => departmentNames.get(Number(id)) || `Department ${id}`}
            rowKey={(r) => r.leave_id}
            emptyMessage="Nothing is waiting here right now."
            exportFileName={`Department_Leave_Approvals_${stage.toUpperCase()}`}
          />
        </>
      )}
    </WarningPage>
  );
};

export default DepartmentLeaveApprovals;
